import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItemButton,
  Typography,
  Badge,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { formatDate } from '../../util/formatHelper';
import UserAvatar from './UserAvatar';

const previewLabel = (conversation, currentUserId) => {
  const lm = conversation.lastMessage;
  if (!lm) return 'Neue Unterhaltung';
  const prefix = String(lm.senderId) === String(currentUserId) ? 'Du: ' : '';
  return `${prefix}${lm.text || ''}`;
};

export default function ConversationList({
  conversations,
  activeConversationId,
  currentUserId,
  onlineUsers,
  onSelect,
}) {
  const [search, setSearch] = useState('');

  const sorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...conversations]
      .filter(c => !c.archived)
      .filter(c => (term ? (c.otherUser?.displayName || '').toLowerCase().includes(term) : true))
      .sort((a, b) => {
        if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
        return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
      });
  }, [conversations, search]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1.5 }}>
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Unterhaltungen durchsuchen'
          size='small'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {sorted.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', mt: 3, px: 2 }}>
            Noch keine Unterhaltungen. Wechsle zu „Kolleg:innen“, um eine zu starten.
          </Typography>
        ) : (
          <List disablePadding>
            {sorted.map(conv => {
              const online =
                (onlineUsers && conv.otherUser && onlineUsers.has(conv.otherUser.id)) ||
                conv.otherUser?.isOnline;
              return (
                <ListItemButton
                  key={conv.id}
                  selected={conv.id === activeConversationId}
                  onClick={() => onSelect(conv)}
                  sx={{ gap: 1.5, py: 1 }}
                >
                  <UserAvatar name={conv.otherUser?.displayName} size={44} online={!!online} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant='subtitle2'
                        sx={{ fontWeight: conv.unreadCount ? 700 : 600, flex: 1 }}
                        noWrap
                      >
                        {conv.otherUser?.displayName || 'Mitarbeiter'}
                      </Typography>
                      {conv.pinned && <PushPinIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                      {conv.muted && (
                        <NotificationsOffOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      )}
                      <Typography variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                        {conv.lastMessageAt ? formatDate(conv.lastMessageAt) : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant='body2'
                        color={conv.unreadCount ? 'text.primary' : 'text.secondary'}
                        sx={{ flex: 1, fontWeight: conv.unreadCount ? 600 : 400 }}
                        noWrap
                      >
                        {previewLabel(conv, currentUserId)}
                      </Typography>
                      {conv.unreadCount > 0 && (
                        <Badge
                          badgeContent={conv.unreadCount}
                          color='primary'
                          sx={{ mr: 1.5 }}
                          max={99}
                        />
                      )}
                    </Box>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}

ConversationList.propTypes = {
  conversations: PropTypes.array,
  activeConversationId: PropTypes.string,
  currentUserId: PropTypes.string,
  onlineUsers: PropTypes.instanceOf(Set),
  onSelect: PropTypes.func.isRequired,
};
